/**
 * View Contract Version Modal
 *
 * Modal for viewing historical contract versions with signatures.
 * Used in VersionHistoryAccordion to allow users to view executed contracts.
 * Read-only display with no actions - purely for viewing/auditing.
 */

import { useRef } from 'react';
import { LwModal } from '@/components/lw';
import { Badge } from '@/components/ui/badge';
import { Download } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { parseAsLocalDate } from '@/utils/dateHelpers';
import { ReadOnlyContractDisplay } from './ReadOnlyContractDisplay';
import type { VersionHistoryItem } from '@/services/contractVersionService';

interface ViewContractVersionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  version: VersionHistoryItem | null;
  candidateName?: string;
}

export const ViewContractVersionModal: React.FC<ViewContractVersionModalProps> = ({
  open,
  onOpenChange,
  version,
  candidateName
}) => {
  const contentRef = useRef<HTMLDivElement>(null);

  if (!open || !version) {
    return null;
  }

  const handlePrint = () => {
    // Open print dialog for the contract content
    const printContent = contentRef.current;
    if (!printContent) {
      return;
    }

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      return;
    }

    const contractTitle = `Contract Version ${version.versionNumber || 1}${candidateName ? ` - ${candidateName}` : ''}`;

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>${contractTitle}</title>
          <style>
            body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; padding: 40px; line-height: 1.6; }
            .contract-section { margin-bottom: 24px; padding: 16px; border: 1px solid #e5e7eb; border-radius: 8px; }
            .contract-section h3 { font-size: 14px; font-weight: 600; margin-bottom: 12px; color: #374151; }
            .signature-block { margin-top: 24px; padding: 16px; background: #f9fafb; border-radius: 8px; }
            .signature-image { max-height: 60px; margin: 8px 0; }
            @media print { body { padding: 20px; } }
          </style>
        </head>
        <body>
          <h1 style="font-size: 24px; margin-bottom: 8px;">${contractTitle}</h1>
          <p style="color: #6b7280; margin-bottom: 24px;">Printed on ${new Date().toLocaleDateString()}</p>
          ${printContent.innerHTML}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  // Determine contract status for display
  const getStatusBadge = () => {
    if (version.voidedAt) {
      return <Badge variant="destructive">Voided</Badge>;
    }
    if (version.hasRecruiterSignature && version.hasLoSignature) {
      return <Badge className="bg-green-600">Fully Executed</Badge>;
    }
    if (version.hasLoSignature) {
      return <Badge className="bg-blue-600">LO Signed</Badge>;
    }
    if (version.status === 'signed') {
      return <Badge className="bg-green-600">Signed</Badge>;
    }
    return <Badge variant="secondary">Draft</Badge>;
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) {
      return null;
    }
    try {
      const date = parseAsLocalDate(dateString);
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return null;
      }
      return date.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.error('Error formatting date:', dateString, error);
      return null;
    }
  };

  const createdAgo = (() => {
    if (!version.createdAt) {
      return null;
    }
    try {
      const date = parseAsLocalDate(version.createdAt);
      if (!isNaN(date.getTime())) {
        return `Created ${formatDistanceToNow(date, { addSuffix: true })}`;
      }
    } catch (error) {
      console.error('Error formatting createdAt date:', version.createdAt, error);
    }
    return null;
  })();

  return (
    <LwModal
      size="tall"
      eyebrow="Contract"
      title={`Contract Version ${version.versionNumber || 1}${candidateName ? ` - ${candidateName}` : ''}`}
      sub={
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          {getStatusBadge()}
          {createdAgo && <span>{createdAgo}</span>}
        </span>
      }
      onClose={() => onOpenChange(false)}
      footer={
        <>
          <button className="btn-sm" onClick={() => onOpenChange(false)} type="button">
            Close
          </button>
          <button className="btn-sm solid" onClick={handlePrint} type="button">
            <Download />
            Export PDF
          </button>
        </>
      }
    >
      <div ref={contentRef} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {/* Voided info bar */}
        {version.voidedAt && (
          <div className="err-box">
            <div>
              <div className="err-title">
                Voided on {formatDate(version.voidedAt)}
                {version.voidedByUser && <> by {version.voidedByUser.full_name || version.voidedByUser.email}</>}
              </div>
              {version.voidReason && <div className="err-detail">Reason: {version.voidReason}</div>}
            </div>
          </div>
        )}

        {/* Signature Status Summary */}
        {(version.hasLoSignature || version.hasRecruiterSignature) && (
          <div className="panel" style={{ padding: 14 }}>
            <p style={{ fontSize: 13, fontWeight: 700, color: 'var(--green-900)', margin: '0 0 8px' }}>Signature Status</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, color: 'var(--ink-soft)' }}>
              {version.hasLoSignature && version.loanOfficerSignatureDate && (
                <p style={{ margin: 0 }}>✓ Loan Officer signed on {formatDate(version.loanOfficerSignatureDate)}</p>
              )}
              {version.hasRecruiterSignature && version.recruiterSignatureDate && (
                <p style={{ margin: 0 }}>✓ Company signed on {formatDate(version.recruiterSignatureDate)}</p>
              )}
              {!version.hasLoSignature && (
                <p style={{ margin: 0, color: 'var(--lw-muted)' }}>○ Awaiting Loan Officer signature</p>
              )}
              {!version.hasRecruiterSignature && (
                <p style={{ margin: 0, color: 'var(--lw-muted)' }}>○ Awaiting Company signature</p>
              )}
            </div>
          </div>
        )}

        {/* Contract Display */}
        <ReadOnlyContractDisplay contractInstance={version} showSignatures={true} />
      </div>
    </LwModal>
  );
};
